import { Trans } from '@lingui/macro';
import { useNavigate } from "react-router-dom"
import { useAuth } from "@shared/hooks/useAuth"
import { AlertCircle, Loader2 } from "lucide-react"
import { Card, CardContent } from "@shared/ui/card"
import { Alert, AlertDescription } from "@shared/ui/alert"
import { ThemeToggle } from "@shared/ui/theme-toggle"
import { Suspense, lazy, useEffect, useState } from "react"

const PatientSignIn = lazy(() => import("@features/auth/pages/patient-sign-in"));

function App() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Handle hash fragment redirects for password reset/recovery and invite links
  useEffect(() => {
    if (window.location.hash) {
      const hashString = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(hashString);
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');

      // Handle password recovery links - redirect to reset password page with the hash
      if (type === 'recovery' && accessToken) {
        setIsRedirecting(true);
        // Navigate to reset password page with the hash fragment preserved
        navigate(`/auth/reset-password${window.location.hash}`, { replace: true });
        return;
      }

      // Handle invite links - redirect to callback with the hash
      if (type === 'invite' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }

      // Handle magic link authentication
      if (type === 'magiclink' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }

      // Handle signup confirmation
      if (type === 'signup' && accessToken) {
        setIsRedirecting(true);
        navigate(`/auth/callback${window.location.hash}`, { replace: true });
        return;
      }
    }
  }, [navigate]);

  // If user is authenticated, redirect to the unified dashboard
  useEffect(() => {
    if (user && !isRedirecting && !window.location.hash) {
      setIsRedirecting(true);
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate, isRedirecting]);

  // Show loading while redirecting to prevent flash
  if (isRedirecting) {
    return (
      <div className="flex min-h-svh webview-safe-screen flex-col items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl border-border/50">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  <Trans id='App.processing-your-request'>Processing your request</Trans>
                </div>
              </div>
              <div className="text-center space-y-3 w-full">
                <p className="text-sm text-muted-foreground">
                  <Trans id='App.redirecting-please-wait'>Redirecting, please wait...</Trans>
                </p>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary via-primary/50 to-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-svh webview-safe-screen flex-col items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl border-border/50">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent"><Trans id='App.initializing-jema-manager'>Initializing Jema Manager</Trans></div>
              </div>
              <div className="text-center space-y-3 w-full">
                <p className="text-sm text-muted-foreground"><Trans id='App.checking-your-authentication-status'>Checking your authentication status...</Trans></p>
                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary via-primary/50 to-primary bg-[length:200%_100%] animate-[shimmer_2s_linear_infinite]"></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Helpful info while loading */}
        <div className="mt-6 max-w-md text-center">
          <Alert className="border-muted-foreground/20 bg-card/50 backdrop-blur-sm">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs"><Trans id='App.taking-longer-than-usual-check'>Taking longer than usual? Check your internet connection or try refreshing the page.</Trans></AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Show loading if user is authenticated but redirect hasn't happened yet
  if (user) {
    return (
      <div className="flex min-h-svh webview-safe-screen flex-col items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md shadow-xl border-border/50">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                  <Trans id='App.redirecting-to-dashboard'>Redirecting to dashboard</Trans>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not authenticated, and no redirect pending: Show Patient Sign In (Root)
  return (
    <Suspense fallback={<LoadingState />}>
      <PatientSignIn />
    </Suspense>
  );
}

export default App

function LoadingState() {
  return (
    <div className="flex min-h-svh webview-safe-screen flex-col items-center justify-center p-8 bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
